import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, Menu, Select, Avatar, Button } from 'antd';
import type { MenuProps } from 'antd';
import styled from 'styled-components';
import { MenuOutlined, UserOutlined, SearchOutlined } from '@ant-design/icons';
import {
  DashboardIcon,
  FinancialIcon,
  HierarchyIcon,
  RiskIcon,
  ControlIcon,
  GapIcon,
  CertificationIcon,
  COSOIcon,
  AuditIcon,
  SystemSettingsIcon,
  LogoutIcon,
  UserManagementIcon
} from './MenuIcons';
import ConfirmModal from '../common/ConfirmModal';
import { AppDispatch, RootState, resetReduxState } from '../../store';
import { setSelectedReportByYear } from '../../store/slices/annualReportSlice';
import { clearChangeLogCache } from '../../store/slices/changeLogSlice';
import { RESPONSIVE_BREAKPOINT } from '../../utils/constants';
import warningIcon from '../../assets/icons/ic-warning.svg';
import { signOut } from '../../services/authService';
import { broadcastLogout } from '../../utils/authEventBroadcast';

const MobileHeader = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: #ffffff;
  border-bottom: 1px solid #F0F0F0;
  padding: 0 16px;
  align-items: center;
  justify-content: space-between;
  z-index: 999;

  @media (max-width: ${RESPONSIVE_BREAKPOINT}) {
    display: flex;
  }
`;

const Logo = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: #2563EB;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const MenuButton = styled(Button)`
  border: none;
  box-shadow: none;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #F3F4F6;
  }
`;

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const FiscalYearSection = styled.div`
  padding: 16px;
`;

const CompanySection = styled.div`
  padding: 8px 16px 0px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  justify-content: center;
  gap: 12px;

  .company-info {
    flex: 1;
    justify-content: center;
    display: flex;

    .company-name {
      font-weight: 600;
      font-size: 16px;
      color: #1F2937;
      margin: 0;
    }
  }
`;

const EmptyCompanySection = styled.div`
  padding: 8px 20px 8px 20px;
  display: flex;
  align-items: center;
  .company-info {
      padding-left:16px;
      justify-content: center;
      display: flex;

      .company-name {
        font-weight: 600;
        font-size: 16px;
        color: #1F2937;
        margin: 0;
      }

      .company-change {
        font-weight: 400;
        font-size: 12px;
        color: #315294;
        margin: 0;
        cursor: pointer;
      }
    }
`;

const CompanyAvatar = styled(Avatar)`
  background: #DBEAFE;
  font-weight: 600;
  flex-shrink: 0;
  border-radius: 8px;
  color: #2563EB;
`;

const CompanyLogo = styled.div`
  max-height: 80px;
  max-width: 160px;
  max-height: 80px;
  max-width: 160px;
  flex-shrink: 0;
  display: flex;
  align-items: center;

  img {
    max-height: 80px;
    max-width: 160px;
    object-fit: contain;
    border-radius: 8px;
  }
`;

const NavigationSection = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0px;
  border-top: 1px solid #F0F0F0;
  border-top: 1px solid #F0F0F0;

  .ant-menu {
    border: none !important;
    border-inline-end: none !important;
    background: transparent;
  }

  .ant-menu-item {
    border-radius: 6px;
    margin: 2px 0;
    height: 48px;
    line-height: 24px;
    font-size: 16px;
    color: #1F2937;

    &:hover {
      background: #F3F4F6;
    }

    &.ant-menu-item-selected {
      background: transparent;
      color: #2563EB;
      font-weight: 500;

      .ant-menu-item-icon {
        color: #2563EB;
        
        svg path {
          stroke: #2563EB;
        }
      }

      &::after {
        display: none;
      }
    }

    .ant-menu-title-content {
      margin-left: 12px !important;
    }
  }

  .ant-menu-item-icon {
    svg {
      width: 20px !important;
      height: 20px !important;
    }
  }

  .ant-menu-item-divider {
    margin: 8px 0;
    background: #E5E7EB;
  }
`;

const UserSection = styled.div`
  padding: 16px;
  border-top: 1px solid #F0F0F0;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .user-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;

    .user-details {
      flex: 1;

      .user-name {
        font-weight: 500;
        font-size: 14px;
        color: #000000;
        margin: 0;
      }

      .user-role {
        font-size: 12px;
        color: #6B7280;
        margin: 0;
      }
    }
  }

  .settings-icon {
    font-size: 18px;
    color: #6B7280;
    cursor: pointer;
    padding: 8px;
    border-radius: 6px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: #EFF6FF;
      color: #2563EB;
    }

    &:active {
      transform: scale(0.95);
    }
  }
`;

const MobileSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAdmin, isExternalUser } = useSelector((state: RootState) => state.auth);
  const { reports, selectedReport } = useSelector((state: RootState) => state.annualReport);
  const [visible, setVisible] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const companyId = localStorage.getItem("companyId");
  const companyName = localStorage.getItem("companyName");
  const companyImageUrl = localStorage.getItem("companyImageUrl");

  // Handle year change
  const handleYearChange = (year: number) => {
    dispatch(clearChangeLogCache()); // Clear change log cache when fiscal year changes
    dispatch(setSelectedReportByYear(year));
  };

  const showDrawer = () => {
    setVisible(true);
  };

  const closeDrawer = () => {
    setVisible(false);
  };

  const handleShowLogoutModal = () => {
    setShowLogoutModal(true);
    closeDrawer();
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    try {
      // Call server-side sign out
      await signOut();
      // Broadcast logout to all tabs
      broadcastLogout('user_initiated');
      // Clear all Redux state with single action
      dispatch(resetReduxState());
      // Navigate to sign in
      navigate('/auth/signin');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear all Redux state even if server call fails
      dispatch(resetReduxState());
      navigate('/auth/signin');
    } finally {
      setIsLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const menuItems: MenuProps['items'] = [
      {
        key: '/search',
        icon: <SearchOutlined style={{ fontSize: '20px' }} />,
        label: 'Search',
      },
      {
        type: 'divider' as const,
      },
      {
        key: '/dashboard',
        icon: <DashboardIcon />,
        label: 'Dashboard Reporting',
      },
      {
        key: '/financial-statements',
        icon: <FinancialIcon />,
        label: 'Financial Statements',
      },
      {
        key: '/process-hierarchy',
        icon: <HierarchyIcon />,
        label: 'Process Hierarchy',
      },
      {
        key: '/risk-assessment',
        icon: <RiskIcon />,
        label: 'Risk Assessment',
      },
      {
        key: '/control-activities',
        icon: <ControlIcon />,
        label: 'Control Activities',
      },
      {
        key: '/gaps',
        icon: <GapIcon />,
        label: 'Gaps',
      },
      {
        key: '/certification',
        icon: <CertificationIcon />,
        label: 'Certification',
      },
      {
        key: '/coso-compliance',
        icon: <COSOIcon />,
        label: 'COSO Compliance',
      },
      // Audit Plan - Only visible to admins
      ...(isAdmin || isExternalUser ? [{
        key: '/audit-plan',
        icon: <AuditIcon />,
        label: 'Audit Plan',
      }] : []),
      {
        type: 'divider' as const,
      },
  
      ...(!isExternalUser ? [{
        key: '/user-management',
        icon: <UserManagementIcon />,
        label: 'User Management',
      }] : []),
      {
        key: '/settings',
        icon: <SystemSettingsIcon />,
        label: 'Settings',
      },
      {
        key: 'logout',
        icon: <LogoutIcon />,
        label: 'Log out',
        onClick: handleShowLogoutModal,
      },
    ];
  
    const limitedMenuItems: MenuProps['items'] = [
      ...(!isExternalUser ? [{
        key: '/user-management',
        icon: <UserManagementIcon />,
        label: 'User Management',
      }] : []),
      {
        key: '/settings',
        icon: <SystemSettingsIcon />,
        label: 'Settings',
      },
      {
        key: 'logout',
        icon: <LogoutIcon />,
        label: 'Log out',
        onClick: handleShowLogoutModal,
      },
    ];

  const handleMenuClick = ({ key }: { key: string }) => {
    // Don't navigate for logout as it has its own onClick handler
    if (key === 'logout') return;
    navigate(key);
    closeDrawer();
  };

  const handleChangeCompanyClick = () => {
    navigate("/companies");
  };

  const handleAccountSettings = () => {
    navigate('/settings');
    closeDrawer();
  };

  // Get active menu key based on current path
  const getActiveMenuKey = () => {
    const path = location.pathname;
    
    // Check for exact matches first
    const menuKeys = [
      '/dashboard',
      '/financial-statements',
      '/process-hierarchy',
      '/risk-assessment',
      '/control-activities',
      '/gaps',
      '/certification',
      '/coso-compliance',
      '/audit-plan',
      '/settings',
    ];
    
    // Check exact match
    if (menuKeys.includes(path)) {
      return path;
    }
    
    // Check if path starts with any menu key (for nested routes)
    for (const key of menuKeys) {
      if (path.startsWith(key)) {
        return key;
      }
    }
    
    return path;
  };

  const currentPath = getActiveMenuKey();

  return (
    <>
      <MobileHeader data-no-highlight>
        <MenuButton icon={<MenuOutlined />} onClick={showDrawer} />
        <Logo>OSA Internal Controls</Logo>
      </MobileHeader>

      <Drawer
        title={null}
        placement="left"
        onClose={closeDrawer}
        open={visible}
        width={300}
        headerStyle={{ padding: '16px' }}
        bodyStyle={{ padding: 0 }}
        rootClassName="no-highlight-drawer"
      >
        <DrawerContent data-no-highlight>
          {companyId != null ? 
                  <div>
                    
                    <CompanySection>
                      <div className="company-info">
                        <a className="company-change" onClick={handleChangeCompanyClick}>Change Company</a>
                      </div>
                    </CompanySection>
                    
                    <CompanySection>
                      <div className="company-info">
                        <p className="company-name">{companyName}</p>
                      </div>
                    </CompanySection>

                    <CompanySection>
                    {companyImageUrl ? (
                      <CompanyLogo>
                        <img
                          src={companyImageUrl}
                          alt={companyName || 'Company'}
                        />
                      </CompanyLogo>
                    ) : (
                      <CompanyAvatar size={40}>
                        {(companyName?.trim()?.charAt(0) || 'C').toUpperCase()}
                      </CompanyAvatar>
                    )}
                    
                    
                  </CompanySection>
                  
                    <FiscalYearSection>
                    
          
                    <Select
                      value={selectedReport?.year}
                      onChange={handleYearChange}
                      style={{ width: '100%' }}
                      options={reports.map(report => ({
                        value: report.year,
                        label: `Fiscal Year ${report.year}`,
                      }))}
                    />
                  </FiscalYearSection>
                  </div>
                  
                  :""}
          
                  {companyId == null ? 
                   <EmptyCompanySection>
                              
                    <CompanyAvatar
                      size={40}
                      src={undefined}
                    >
                      {('C').toUpperCase()}
                    </CompanyAvatar>
                    <div className="company-info">
                      <a className="company-change" onClick={handleChangeCompanyClick}>Choose Company</a>
                    </div>
                  </EmptyCompanySection>
                  : ""}
        {companyId != null ? 
        <NavigationSection>
          <Menu
            mode="inline"
            selectedKeys={[currentPath]}
            items={menuItems}
            onClick={handleMenuClick}
          />
        </NavigationSection>
        :
        <NavigationSection>
          <Menu
            mode="inline"
            selectedKeys={[currentPath]}
            items={limitedMenuItems}
            onClick={handleMenuClick}
          />
        </NavigationSection>
        }

          <UserSection>
            <div className="user-info">
              <Avatar 
                size={36} 
                icon={<UserOutlined />}
                style={{ borderRadius: '8px' }}
              />
              <div className="user-details">
                <p className="user-name">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="user-role">{user?.userType}</p>
              </div>
            </div>
            <div 
              className="settings-icon" 
              onClick={handleAccountSettings}
            >
              <SystemSettingsIcon />
            </div>
          </UserSection>
        </DrawerContent>
      </Drawer>

      <ConfirmModal
        open={showLogoutModal}
        onClose={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
        icon={warningIcon}
        title="Sign out"
        description="Are you sure you would like to sign out of your OSA Internal Controls account?"
        confirmText="Sign out"
        cancelText="Cancel"
        loading={isLoggingOut}
      />
    </>
  );
};

export default MobileSidebar;

